import type { LoginDeviceItemDto, SecurityLogItemDto } from '~/types/api/security'
import type { SecDevice, SecLog } from '../types'

const EVENT_TITLE_KEYS: Record<string, string> = {
  login_success: 'logLoginOk',
  new_device_login: 'logNewDevice',
  login_failed: 'logLoginFail',
  password_changed: 'logPwdChanged',
  password_reset: 'logPwdReset',
  fund_password_set: 'logFundSet',
  fund_password_changed: 'logFundChanged',
  fund_password_reset: 'logFundReset',
  fund_password_verify_failed: 'logFundVerifyFail',
  phone_bound: 'logPhoneBound',
  phone_changed: 'logPhoneChanged',
  email_bound: 'logEmailBound',
  email_changed: 'logEmailChanged',
  device_revoked: 'logDeviceKick',
  withdraw_submitted: 'logWithdrawSubmit',
}

type TranslateFn = (key: string, ...args: unknown[]) => string

const joinDot = (...parts: Array<string | undefined>) => parts.filter(Boolean).join(' · ')

function maskIp(ip: string): string {
  const trimmed = ip.trim()
  if (!trimmed) return ''
  const parts = trimmed.split('.')
  if (parts.length === 4) return `${parts[0]}.${parts[1]}.*.*`
  if (trimmed.length > 12) return `${trimmed.slice(0, 8)}…`
  return trimmed
}

function deviceLabel(
  item: Pick<
    SecurityLogItemDto,
    'device_model' | 'browser_name' | 'os_name' | 'os_version' | 'device_type'
  >,
  fallback = '',
): string {
  const model = item.device_model?.trim() || ''
  const browser = item.browser_name?.trim() || ''
  const os = [item.os_name?.trim(), item.os_version?.trim()].filter(Boolean).join(' ')
  return model || joinDot(browser, os) || item.device_type?.trim() || fallback
}

function deviceIcon(item: LoginDeviceItemDto): string {
  const os = `${item.os_name} ${item.device_model}`.toLowerCase()
  if (os.includes('android')) return '🤖'
  if (item.device_type === 'desktop') return '💻'
  if (item.device_type === 'mobile' || item.device_type === 'tablet') return '📱'
  if (item.browser_name?.trim()) return '💻'
  return '📱'
}

function logDetail(item: SecurityLogItemDto, t: TranslateFn): string {
  let reason: string | undefined
  if (item.result === 'blocked' && item.reason) {
    const key = `safe.logReason.${item.reason}`
    const text = t(key)
    reason = text !== key ? text : item.reason
  }

  const current = item.is_current ? t('safe.logLocalAction') : undefined
  if (item.event === 'withdraw_submitted' && item.amount) {
    const num = Number(item.amount)
    const cur = (item.currency ?? '').trim().toUpperCase()
    const display = Number.isFinite(num) ? num.toFixed(2) : item.amount
    const amount =
      cur === 'CNY' || cur === 'RMB' ? `¥${display}` : cur ? `${display} ${cur}` : display
    return joinDot(reason, amount, current)
  }

  return joinDot(
    reason,
    deviceLabel(item),
    item.city?.trim() || item.country?.trim(),
    item.ip ? `IP ${maskIp(item.ip)}` : undefined,
    current,
  )
}

export function mapSecurityLog(item: SecurityLogItemDto, t: TranslateFn): SecLog {
  return {
    id: `${item.created_at}-${item.event}-${item.ref_id ?? ''}-${item.ip ?? ''}`,
    titleKey: EVENT_TITLE_KEYS[item.event] ?? 'logUnknown',
    time: item.created_at,
    detail: logDetail(item, t),
    ok: item.result !== 'blocked',
  }
}

/** 本机置顶，其余保持接口登录时间倒序 */
export function mapLoginDevices(items: LoginDeviceItemDto[], t: TranslateFn): SecDevice[] {
  return items
    .map(item => {
      const current = Boolean(item.is_current)
      const loc = item.city?.trim() || item.country?.trim()
      const ip = item.ip ? `IP ${maskIp(item.ip)}` : ''
      return {
        id: item.session_id,
        icon: deviceIcon(item),
        name: joinDot(deviceLabel(item, '—'), loc),
        time: current ? undefined : item.created_at,
        detail: current ? t('safe.deviceCurrent') : ip,
        current,
      }
    })
    .sort((a, b) => Number(!!b.current) - Number(!!a.current))
}
